/**
 * CostDashboardPage Component
 *
 * Main dashboard for cost tracking and budget management
 * Follows: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, Button, Select, Spinner, Table, Badge } from 'flowbite-react';
import { ArrowDownTrayIcon, FunnelIcon, CalendarIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import CostSummaryCard from '../components/CostSummaryCard';
import CostBreakdownChart, { ChartDataPoint } from '../components/CostBreakdownChart';
import BudgetAlertBanner, { BudgetAlert } from '../components/BudgetAlertBanner';
import {
  getClientCosts,
  getCompanyCosts,
  getProjectCosts,
  exportCostData,
  formatCurrency,
  getUtilizationColor,
  getDateRangeForPeriod,
  TimePeriod,
  ExportFormat,
  ClientCost,
  CompanyCost,
  ProjectCost
} from '../api/costs';

export const CostDashboardPage: React.FC = () => {
  const { user } = useAuth();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('this_month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedView, setSelectedView] = useState<'client' | 'company' | 'project'>('client');
  const [exportFormat, setExportFormat] = useState<ExportFormat>('csv');
  const [isExporting, setIsExporting] = useState(false);

  // Data state
  const [clientCosts, setClientCosts] = useState<ClientCost[]>([]);
  const [companyCosts, setCompanyCosts] = useState<CompanyCost[]>([]);
  const [projectCosts, setProjectCosts] = useState<ProjectCost[]>([]);

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [budgetStatusFilter, setBudgetStatusFilter] = useState<'all' | 'under_budget' | 'at_budget' | 'over_budget'>('all');

  // Get date range based on selected period
  const dateRange = useMemo(() => {
    return getDateRangeForPeriod(timePeriod, customStartDate, customEndDate);
  }, [timePeriod, customStartDate, customEndDate]);

  // Fetch cost data
  const fetchCostData = useCallback(async () => {
    if (!user?.client_id) {
      toast.error('No client context available');
      return;
    }

    setIsLoading(true);
    try {
      const params = {
        period_start: dateRange.start,
        period_end: dateRange.end,
        include_breakdown: true,
        include_trends: true
      };

      // Fetch based on selected view
      if (selectedView === 'client') {
        const response = await getClientCosts(user.client_id, params);
        if (response.success) {
          setClientCosts(response.data.Records);
        }
      } else if (selectedView === 'company') {
        const response = await getCompanyCosts(user.client_id, undefined, params);
        if (response.success) {
          setCompanyCosts(response.data.Records);
        }
      } else if (selectedView === 'project') {
        const response = await getProjectCosts(user.client_id, undefined, undefined, params);
        if (response.success) {
          setProjectCosts(response.data.Records);
        }
      }
    } catch (error) {
      console.error('Failed to fetch cost data:', error);
      toast.error('Failed to load cost data');
    } finally {
      setIsLoading(false);
    }
  }, [dateRange, selectedView, user]);

  useEffect(() => {
    fetchCostData();
  }, [fetchCostData]);

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    let totalCost = 0;
    let totalBudget = 0;
    let totalExecutions = 0;

    if (selectedView === 'client' && clientCosts.length > 0) {
      const client = clientCosts[0];
      totalCost = client.Total_Cost;
      totalBudget = client.Budget_Allocated;
      totalExecutions = client.Execution_Count;
    } else if (selectedView === 'company') {
      companyCosts.forEach(company => {
        totalCost += company.Total_Cost;
        totalBudget += company.Budget_Allocated;
        totalExecutions += company.Execution_Count;
      });
    } else if (selectedView === 'project') {
      projectCosts.forEach(project => {
        totalCost += project.Total_Cost;
        totalBudget += project.Budget_Allocated;
        totalExecutions += project.Execution_Count;
      });
    }

    return {
      totalCost,
      totalBudget,
      utilizationPct: totalBudget > 0 ? (totalCost / totalBudget) * 100 : 0,
      totalExecutions
    };
  }, [selectedView, clientCosts, companyCosts, projectCosts]);

  // Get budget alerts
  const budgetAlerts = useMemo((): BudgetAlert[] => {
    const alerts: BudgetAlert[] = [];

    if (selectedView === 'company') {
      companyCosts.forEach(company => {
        if (company.Status === 'over_budget') {
          alerts.push({
            id: company.Company_ID,
            type: 'company',
            name: company.Company_Name,
            totalCost: company.Total_Cost,
            budgetCost: company.Budget_Allocated,
            utilizationPct: company.Utilization_Percentage,
            overBudgetAmount: company.Total_Cost - company.Budget_Allocated
          });
        }
      });
    } else if (selectedView === 'project') {
      projectCosts.forEach(project => {
        if (project.Status === 'over_budget') {
          alerts.push({
            id: project.Project_ID,
            type: 'project',
            name: project.Project_Name,
            totalCost: project.Total_Cost,
            budgetCost: project.Budget_Allocated,
            utilizationPct: project.Utilization_Percentage,
            overBudgetAmount: project.Total_Cost - project.Budget_Allocated
          });
        }
      });
    }

    return alerts.sort((a, b) => b.utilizationPct - a.utilizationPct);
  }, [selectedView, companyCosts, projectCosts]);

  // Prepare chart data
  const chartData = useMemo((): ChartDataPoint[] => {
    if (selectedView === 'company') {
      return companyCosts.map(company => ({
        name: company.Company_Name,
        value: company.Total_Cost,
        budget: company.Budget_Allocated,
        executions: company.Execution_Count,
        percentage: summaryMetrics.totalCost > 0 ? (company.Total_Cost / summaryMetrics.totalCost) * 100 : 0
      }));
    } else if (selectedView === 'project') {
      return projectCosts.map(project => ({
        name: project.Project_Name,
        value: project.Total_Cost,
        budget: project.Budget_Allocated,
        executions: project.Execution_Count,
        percentage: summaryMetrics.totalCost > 0 ? (project.Total_Cost / summaryMetrics.totalCost) * 100 : 0
      }));
    }
    return [];
  }, [selectedView, companyCosts, projectCosts, summaryMetrics.totalCost]);

  // Filter data based on budget status
  const filteredTableData = useMemo(() => {
    let data: Array<CompanyCost | ProjectCost> = selectedView === 'company' ? companyCosts : projectCosts;

    if (budgetStatusFilter !== 'all') {
      data = data.filter(item => item.Status === budgetStatusFilter);
    }

    return data;
  }, [selectedView, companyCosts, projectCosts, budgetStatusFilter]);

  // Handle export
  const handleExport = async () => {
    if (!user?.client_id) return;

    setIsExporting(true);
    try {
      const blob = await exportCostData(user.client_id, exportFormat, {
        period_start: dateRange.start,
        period_end: dateRange.end
      });

      // Download file
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cost-report-${dateRange.start}-to-${dateRange.end}.${exportFormat}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Cost report exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export cost report');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cost Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Track costs and budget utilization across your organization
          </p>
        </div>

        {/* Export Button */}
        <div className="flex gap-3">
          <Select
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
            className="w-32"
          >
            <option value="csv">CSV</option>
            <option value="json">JSON</option>
            <option value="xlsx">Excel</option>
          </Select>
          <Button
            color="blue"
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? (
              <Spinner size="sm" className="mr-2" />
            ) : (
              <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
            )}
            Export
          </Button>
        </div>
      </div>

      {/* Budget Alerts */}
      {budgetAlerts.length > 0 && (
        <BudgetAlertBanner alerts={budgetAlerts} linkToCostDashboard={false} />
      )}

      {/* Filters */}
      <Card>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
            <Button
              size="sm"
              color="light"
              onClick={() => setShowFilters(!showFilters)}
            >
              <FunnelIcon className="w-4 h-4 mr-2" />
              {showFilters ? 'Hide' : 'Show'} Filters
            </Button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
              {/* Time Period */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <CalendarIcon className="w-4 h-4 inline mr-1" />
                  Time Period
                </label>
                <Select
                  value={timePeriod}
                  onChange={(e) => setTimePeriod(e.target.value as TimePeriod)}
                >
                  <option value="this_month">This Month</option>
                  <option value="last_month">Last Month</option>
                  <option value="last_quarter">Last Quarter</option>
                  <option value="last_year">Last Year</option>
                  <option value="custom">Custom Range</option>
                </Select>
              </div>

              {/* View Level */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  View By
                </label>
                <Select
                  value={selectedView}
                  onChange={(e) => setSelectedView(e.target.value as 'client' | 'company' | 'project')}
                >
                  <option value="client">Client Level</option>
                  <option value="company">Company Level</option>
                  <option value="project">Project Level</option>
                </Select>
              </div>

              {/* Budget Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Budget Status
                </label>
                <Select
                  value={budgetStatusFilter}
                  onChange={(e) => setBudgetStatusFilter(e.target.value as any)}
                >
                  <option value="all">All</option>
                  <option value="under_budget">Under Budget</option>
                  <option value="at_budget">At Budget</option>
                  <option value="over_budget">Over Budget</option>
                </Select>
              </div>

              {/* Custom Date Range */}
              {timePeriod === 'custom' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <CostSummaryCard
          title="Total Costs"
          totalCost={summaryMetrics.totalCost}
          budgetCost={summaryMetrics.totalBudget}
          utilizationPct={summaryMetrics.utilizationPct}
          executionCount={summaryMetrics.totalExecutions}
          isLoading={isLoading}
        />
        <CostSummaryCard
          title="Average Cost per Execution"
          totalCost={summaryMetrics.totalExecutions > 0 ? summaryMetrics.totalCost / summaryMetrics.totalExecutions : 0}
          budgetCost={summaryMetrics.totalExecutions > 0 ? summaryMetrics.totalBudget / summaryMetrics.totalExecutions : 0}
          utilizationPct={summaryMetrics.utilizationPct}
          executionCount={summaryMetrics.totalExecutions}
          isLoading={isLoading}
        />
        <CostSummaryCard
          title="Remaining Budget"
          totalCost={Math.max(0, summaryMetrics.totalBudget - summaryMetrics.totalCost)}
          budgetCost={summaryMetrics.totalBudget}
          utilizationPct={100 - summaryMetrics.utilizationPct}
          executionCount={summaryMetrics.totalExecutions}
          isLoading={isLoading}
        />
      </div>

      {/* Charts */}
      {selectedView !== 'client' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <CostBreakdownChart
            title={`Cost Breakdown by ${selectedView === 'company' ? 'Company' : 'Project'}`}
            data={chartData}
            chartType="bar"
            breakdownType={selectedView}
            isLoading={isLoading}
          />
          <CostBreakdownChart
            title={`Cost Distribution by ${selectedView === 'company' ? 'Company' : 'Project'}`}
            data={chartData}
            chartType="pie"
            breakdownType={selectedView}
            isLoading={isLoading}
          />
        </div>
      )}

      {/* Detailed Table */}
      {selectedView !== 'client' && (
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Top {selectedView === 'company' ? 'Companies' : 'Projects'} by Cost
          </h3>

          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <Spinner size="xl" />
            </div>
          ) : filteredTableData.length === 0 ? (
            <div className="flex justify-center items-center h-64 text-gray-500">
              No data available for the selected filters
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table hoverable>
                <Table.Head>
                  <Table.HeadCell>{selectedView === 'company' ? 'Company' : 'Project'}</Table.HeadCell>
                  <Table.HeadCell>Total Cost</Table.HeadCell>
                  <Table.HeadCell>Budget</Table.HeadCell>
                  <Table.HeadCell>Utilization</Table.HeadCell>
                  <Table.HeadCell>Executions</Table.HeadCell>
                  <Table.HeadCell>Status</Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y">
                  {filteredTableData.map((item) => {
                    const name = 'Company_Name' in item ? item.Company_Name : (item as any).Project_Name;
                    const id = 'Company_ID' in item ? item.Company_ID : (item as any).Project_ID;
                    const statusColor = getUtilizationColor(item.Utilization_Percentage);

                    return (
                      <Table.Row key={id} className="bg-white">
                        <Table.Cell className="font-medium text-gray-900">
                          {name}
                        </Table.Cell>
                        <Table.Cell>{formatCurrency(item.Total_Cost)}</Table.Cell>
                        <Table.Cell>{formatCurrency(item.Budget_Allocated)}</Table.Cell>
                        <Table.Cell>
                          <div className="flex items-center gap-2">
                            <div className="w-24 bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${statusColor === 'green' ? 'bg-green-500' : statusColor === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'}`}
                                style={{ width: `${Math.min(item.Utilization_Percentage, 100)}%` }}
                              ></div>
                            </div>
                            <span className="text-sm text-gray-700">
                              {item.Utilization_Percentage.toFixed(1)}%
                            </span>
                          </div>
                        </Table.Cell>
                        <Table.Cell>{item.Execution_Count.toLocaleString()}</Table.Cell>
                        <Table.Cell>
                          <Badge color={statusColor === 'green' ? 'success' : statusColor === 'yellow' ? 'warning' : 'failure'}>
                            {item.Status.replace('_', ' ')}
                          </Badge>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default CostDashboardPage;
